import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "@/contexts/NotificationContext";
import type { AccessDeniedLocationState, UnknownRouteLocationState } from "@/utils/accessDenied";

type KickHomeLocationState = AccessDeniedLocationState & UnknownRouteLocationState;

/** Same kick can pass through `/` then default home — only toast once. */
const seenKickAt = new Set<number>();

/**
 * Reads kick-home location state (no page_access, or a URL with no route)
 * and shows a floating notice, then clears the state so a refresh is quiet.
 */
export default function useKickHomeNotice() {
  const location = useLocation();
  const navigate = useNavigate();
  const { notice } = useNotification();

  useEffect(() => {
    const state = location.state as KickHomeLocationState | null;
    const unknown = state?.unknownRoute;
    const denied = state?.accessDenied;
    if (!unknown && !denied) return;

    const at = unknown?.at ?? denied?.at;
    if (at != null && seenKickAt.has(at)) {
      navigate(location.pathname + location.search + location.hash, { replace: true, state: {} });
      return;
    }
    if (at != null) seenKickAt.add(at);

    notice(
      unknown
        ? {
            type: "warning",
            title: "Not an accessible page",
            subtext: "You were sent to the home page because that is not an accessible page.",
            duration: 7000,
          }
        : {
            type: "warning",
            title: "Access denied",
            subtext:
              "You were sent home because you do not have access to the page you tried to open.",
            duration: 7000,
          },
    );
    navigate(location.pathname + location.search + location.hash, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, location.hash, notice, navigate]);
}
